/**
 * FileUploadZone
 *
 * Drag-and-drop upload area with file staging, search, and progress bars.
 * Files are staged in a searchable list before uploading.
 */

import React, { useState, useCallback, useRef, useMemo } from 'react';
import { Upload, X, Search, File as FileIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { useFileStore } from '@/stores/files/file-store';
import { FileService } from '@/services/api/fileService';
import * as errorUtils from '@/utils/errors';

interface FileUploadZoneProps {
  orgSlug: string;
  scope?: 'private' | 'project' | 'shared';
  projectId?: string;
  defaultEncryption?: 'none' | 'org' | 'user_protected' | 'per_file';
  folder?: string;
  onUploadComplete?: () => void;
  allowedFileTypes?: string[];
  onFilesRejected?: (filenames: string[]) => void;
  maxFiles?: number;
}

const EMPTY_ALLOWED_TYPES: string[] = [];

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
}

function isFileAllowed(file: File, allowedTypes: string[]): boolean {
  if (allowedTypes.length === 0) return true;
  const mime = file.type || 'application/octet-stream';
  return allowedTypes.some(pattern => {
    if (pattern.startsWith('.')) {
      return file.name.toLowerCase().endsWith(pattern.toLowerCase());
    }
    if (pattern.endsWith('/*')) {
      return mime.startsWith(pattern.slice(0, -1));
    }
    return mime === pattern;
  });
}

export const FileUploadZone: React.FC<FileUploadZoneProps> = ({
  orgSlug,
  scope = 'private',
  projectId,
  defaultEncryption = 'none',
  folder,
  onUploadComplete,
  allowedFileTypes = EMPTY_ALLOWED_TYPES,
  onFilesRejected,
  maxFiles,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
  const encryptionType = defaultEncryption;
  const passphrase = '';

  const { uploads, addUpload, updateUpload, removeUpload } = useFileStore();

  // Filter staged files by search query
  const filteredFiles = useMemo(() => {
    if (!searchQuery) return stagedFiles;
    return stagedFiles.filter(f =>
      f.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [stagedFiles, searchQuery]);

  const toggleFileSelection = useCallback((index: number) => {
    setSelectedIndices(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedIndices.size === filteredFiles.length && filteredFiles.length > 0) {
      setSelectedIndices(new Set());
    } else {
      setSelectedIndices(new Set(filteredFiles.map((_, i) => i)));
    }
  }, [selectedIndices.size, filteredFiles]);

  const clearStagedFiles = useCallback(() => {
    setStagedFiles([]);
    setSearchQuery('');
    setSelectedIndices(new Set());
  }, []);

  const uploadFile = useCallback(async (file: File) => {
    const uploadId = `upload-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    addUpload({
      id: uploadId,
      filename: file.name,
      progress: 0,
      status: 'pending',
    });

    try {
      // Step 1: Get presigned URL
      updateUpload(uploadId, { status: 'uploading' });
      const urlResponse = await FileService.requestUploadUrl(orgSlug, {
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        size: file.size,
        scope,
        projectId,
        encryptionType,
        passphrase: encryptionType === 'user_protected' ? passphrase : undefined,
        folder: folder || '',
      });

      const { fileId, uploadUrl, headers } = urlResponse.data!;
      updateUpload(uploadId, { fileId });

      // Step 2: Upload to presigned URL
      const { etag } = await FileService.uploadToPresignedUrl(
        uploadUrl,
        file,
        file.type || 'application/octet-stream',
        headers,
        (progress) => updateUpload(uploadId, { progress })
      );

      // Step 3: Confirm
      updateUpload(uploadId, { status: 'confirming', progress: 100 });
      await FileService.confirmUpload(orgSlug, fileId, etag);
      updateUpload(uploadId, { status: 'complete' });
      onUploadComplete?.();
    } catch (error: unknown) {
      updateUpload(uploadId, {
        status: 'error',
        error: errorUtils.getApiErrorMessage(error, 'Upload failed'),
      });
    }
  }, [orgSlug, scope, projectId, encryptionType, passphrase, folder, addUpload, updateUpload, onUploadComplete]);

  const handleUploadSelected = useCallback(() => {
    const filesToUpload = filteredFiles.filter((_, i) => selectedIndices.has(i));
    filesToUpload.forEach(uploadFile);
    clearStagedFiles();
  }, [filteredFiles, selectedIndices, uploadFile, clearStagedFiles]);

  // Stage files from native picker or drag-and-drop (appends to existing staged files)
  const stageFiles = useCallback((fileList: FileList | null) => {
    if (!fileList) return;
    const all = Array.from(fileList);
    let accepted = all.filter(f => isFileAllowed(f, allowedFileTypes));
    const rejected = all.filter(f => !isFileAllowed(f, allowedFileTypes));

    // Enforce maxFiles limit
    if (maxFiles !== undefined) {
      const activeUploads = uploads.filter(u => u.status !== 'error').length;
      const existingStaged = stagedFiles.length;
      const remaining = Math.max(0, maxFiles - activeUploads - existingStaged);
      if (accepted.length > remaining) {
        const overflow = accepted.slice(remaining);
        rejected.push(...overflow);
        accepted = accepted.slice(0, remaining);
      }
    }

    if (rejected.length > 0) {
      onFilesRejected?.(rejected.map(f => f.name));
    }

    if (accepted.length > 0) {
      // Deduplicate by name+size against already-staged files
      const existing = new Set(stagedFiles.map(f => `${f.name}:${f.size}`));
      const newFiles = accepted.filter(f => !existing.has(`${f.name}:${f.size}`));
      if (newFiles.length > 0) {
        setStagedFiles(prev => [...prev, ...newFiles]);
        // Auto-select newly added files
        setSelectedIndices(prev => {
          const next = new Set(prev);
          const offset = stagedFiles.length;
          newFiles.forEach((_, i) => next.add(offset + i));
          return next;
        });
      }
    }
  }, [allowedFileTypes, onFilesRejected, maxFiles, uploads, stagedFiles]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    stageFiles(e.dataTransfer.files);
  }, [stageFiles]);

  return (
    <div className="space-y-3">
      {/* Drop Zone */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
          isDragging
            ? 'border-primary bg-primary/10'
            : 'border-muted-foreground/30 bg-muted/20 hover:border-primary/50 hover:bg-muted/30'
        }`}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          Drag and drop files here, or click to browse
        </p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          accept={allowedFileTypes.length > 0 ? allowedFileTypes.join(',') : undefined}
          onChange={(e) => { stageFiles(e.target.files); e.target.value = ''; }}
        />
      </div>

      {/* Staged Files Browser */}
      {stagedFiles.length > 0 && (
        <div className="border border-border rounded-lg overflow-hidden bg-card">
          {/* Search + Actions Bar */}
          <div className="flex items-center gap-2 p-2 bg-muted/30 border-b border-border">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search files..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setSelectedIndices(new Set()); }}
                className="h-7 pl-8 text-xs bg-background border-border"
              />
            </div>
            <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={toggleSelectAll}>
              {selectedIndices.size === filteredFiles.length && filteredFiles.length > 0 ? 'Deselect All' : 'Select All'}
            </Button>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={clearStagedFiles} title="Clear all">
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>

          {/* File List */}
          <div className="max-h-[200px] overflow-y-auto divide-y divide-border">
            {filteredFiles.length === 0 ? (
              <div className="py-6 text-center text-xs text-muted-foreground">
                No files match your search
              </div>
            ) : (
              filteredFiles.map((file, index) => (
                <label
                  key={`${file.name}-${file.size}-${index}`}
                  className="flex items-center gap-2.5 px-3 py-1.5 hover:bg-muted/40 cursor-pointer transition-colors"
                >
                  <Checkbox
                    checked={selectedIndices.has(index)}
                    onCheckedChange={() => toggleFileSelection(index)}
                    className="h-3.5 w-3.5"
                  />
                  <FileIcon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  <span className="text-xs text-foreground truncate flex-1">{file.name}</span>
                  <span className="text-xs text-muted-foreground shrink-0">{formatFileSize(file.size)}</span>
                </label>
              ))
            )}
          </div>

          {/* Upload Selected Footer */}
          <div className="flex items-center justify-between p-2 bg-muted/30 border-t border-border">
            <span className="text-xs text-muted-foreground">
              {selectedIndices.size} of {filteredFiles.length} file{filteredFiles.length !== 1 ? 's' : ''} selected
            </span>
            <Button
              size="sm"
              className="h-7 text-xs gap-1"
              onClick={handleUploadSelected}
              disabled={selectedIndices.size === 0}
            >
              <Upload className="w-3 h-3" />
              Upload Selected
            </Button>
          </div>
        </div>
      )}

      {/* Upload Progress */}
      {uploads.length > 0 && (
        <div className="space-y-2">
          {uploads.map((upload) => (
            <div key={upload.id} className="flex items-center gap-3 bg-muted/40 border border-border rounded p-2">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{upload.filename}</p>
                <div className="flex items-center gap-2">
                  <Progress value={upload.progress} className="flex-1 h-1.5" />
                  <span className="text-xs text-muted-foreground w-10 text-right">
                    {upload.progress}%
                  </span>
                </div>
              </div>
              <Badge variant={
                upload.status === 'complete' ? 'default' :
                upload.status === 'error' ? 'destructive' : 'outline'
              } className="text-xs">
                {upload.status}
              </Badge>
              {(upload.status === 'complete' || upload.status === 'error') && (
                <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => removeUpload(upload.id)}>
                  <X className="w-3 h-3" />
                </Button>
              )}
              {upload.error && (
                <p className="text-xs text-destructive mt-1">{upload.error}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
